// Shared vocabulary of My HR › My Payslips (UI-3B). Every amount and every state is the server's; this file only
// says them in an employee's words. A payslip is not a payment: release, handover and receipt stay separate.

import { formatMoney } from "../../../../components/ui/Money";
import { AGENCY_NAME, APPARIFY_ROUNDING_NOTE, basisText, employerRows, isDecided, shortRuleName, wasRoundedByApparify } from "../contributionsView";
import { dateRange, shortDate } from "../payroll/hrPayroll";

export const MY_PAYSLIPS_BASE = "/business/hr/my-payslips";
export const MY_HR_PATH = "/business/hr/me";
export const payslipPath = (id) => `${MY_PAYSLIPS_BASE}/${id}`;
export const PAGE_SIZE = 12;

const lower = (value) => String(value || "").trim().toLowerCase();

export const periodDates = (payslip) => dateRange({ start_date: payslip?.period_start_date, end_date: payslip?.period_end_date });
export const periodLabel = (payslip) => payslip?.period_name || periodDates(payslip) || "Pay period";
export const payDate = (payslip) => shortDate(payslip?.period_pay_date);

export function dateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-PH", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * Where the salary of a payslip stands (FIN-2). Finance handing the cash over is not payment; the salary is paid
 * once receipt is confirmed. `key`: pending | confirm | paid | partial.
 */
export function paymentOf(payslip) {
  const payment = payslip?.salary_payment;
  if (payment?.status === "awaiting_acknowledgement") {
    const amount = formatMoney(payment.amountCents);
    const when = dateTime(payment.handedOverAt);
    return {
      key: "confirm",
      label: "Confirm receipt",
      // On the payslip itself the line states where the money is, not what to press.
      state: "Cash handed over, not yet confirmed by you",
      tone: "warning",
      amountCents: payment.amountCents,
      detail: `${payment.handedOverByName || "Finance"} recorded handing you ${amount} in cash${when ? ` on ${when}` : ""}. Confirm once you have received it.`,
    };
  }
  if (payment?.status === "completed") {
    const amount = formatMoney(payment.amountCents);
    const when = dateTime(payment.acknowledgedAt);
    const onBehalf = payment.acknowledgementKind === "owner_on_behalf";
    return {
      key: "paid",
      label: "Paid",
      tone: "success",
      date: shortDate(payment.acknowledgedAt),
      amountCents: payment.amountCents,
      detail: onBehalf
        ? `The owner confirmed for you${when ? ` on ${when}` : ""} that you received ${amount} in cash.${payment.onBehalfReason ? ` Reason: ${payment.onBehalfReason}` : ""}`
        : `Paid in cash. You confirmed receiving ${amount}${when ? ` on ${when}` : ""}.`,
    };
  }
  // Net pay of ₱0 (APP-FIN-014): nothing was handed over, so this is never shown as a payment.
  if (payslip?.salary_no_cash_due) {
    return { key: "none", label: "No cash due", tone: "neutral", detail: "Net pay for this payslip is ₱0, so there is no cash to collect." };
  }
  const record = lower(payslip?.payout_record_status);
  if (record === "settled") {
    return { key: "paid", label: "Paid", tone: "success", date: shortDate(payslip?.payout_settled_at), detail: "Your salary for this payslip has been paid." };
  }
  if (record === "partially_settled") {
    return { key: "partial", label: "Partly paid", tone: "neutral", detail: "Part of your salary for this payslip has been paid." };
  }
  return {
    key: "pending",
    label: "Payment pending",
    tone: "neutral",
    detail: "Your payslip is ready. Finance has not recorded handing you this salary yet.",
  };
}

/** What happened after a confirmation, in the employee's words. */
export function confirmedMessage({ payslip, amountCents, alreadyConfirmed }) {
  return alreadyConfirmed
    ? `Your salary for ${periodLabel(payslip)} was already confirmed as received. Nothing changed.`
    : `You confirmed receiving ${formatMoney(amountCents)} for ${periodLabel(payslip)}. Your salary is now marked as paid.`;
}

const CONTRIBUTION_LABEL = { SSS: "SSS contribution", PHILHEALTH: "PhilHealth contribution", PAGIBIG: "Pag-IBIG contribution" };
const REVIEWED_NOTE = "Amount reviewed by HR";

/** The withholding tax line (CD-S3), or null on a payslip calculated before tax was withheld. */
export function withholdingRow(statutory) {
  const line = statutory?.withholding;
  if (!line) return null;
  const decided = isDecided(line);
  const cents = Number(line.employeeCents || 0);
  let note;
  if (!decided) note = undefined;
  else if (line.state === "MANUALLY_REVIEWED") note = REVIEWED_NOTE;
  else if (cents === 0) note = "No tax is withheld on this much taxable pay.";
  return { key: "BIR_WHT", label: "Withholding tax", cents, text: decided ? undefined : "Not decided yet", note };
}

/** Everything taken from the employee's pay, in the order a payslip lists it. Employer amounts are never here. */
export function deductionRows(payslip) {
  const statutory = payslip?.statutory;
  const rows = (statutory?.lines || []).map((line) => ({
    key: line.agency,
    label: CONTRIBUTION_LABEL[line.agency] || AGENCY_NAME[line.agency] || line.agency,
    cents: Number(line.employeeCents || 0),
    text: isDecided(line) ? undefined : "Not decided yet",
    note: line.state === "MANUALLY_REVIEWED" ? REVIEWED_NOTE : undefined,
  }));
  const tax = withholdingRow(statutory);
  if (tax) rows.push(tax);
  for (const deduction of Array.isArray(payslip?.deductions) ? payslip.deductions : []) {
    rows.push({ key: `other-${deduction.id}`, label: deduction.deduction_name || "Deduction", cents: Number(deduction.amount_cents || 0) });
  }
  return rows;
}

/** What the employer pays on top of the employee's pay. Never deducted, never part of net pay. */
export function employerPaid(payslip) {
  const statutory = payslip?.statutory;
  return { rows: employerRows(statutory?.lines || []), totalCents: Number(statutory?.employerTotalCents || 0) };
}

/** How each contribution and the tax were worked out, in one plain line each. No reviewer, code or rule id. */
export function workedOut(payslip) {
  const statutory = payslip?.statutory;
  const items = (statutory?.lines || []).map((line) => ({
    key: line.agency,
    label: AGENCY_NAME[line.agency] || line.agency,
    text: basisText(line) || "Entered by HR after review.",
    rule: line.ruleTitle ? shortRuleName(line.ruleTitle) : null,
    rounding: wasRoundedByApparify(line) ? APPARIFY_ROUNDING_NOTE : null,
  }));
  const tax = statutory?.withholding;
  if (tax && isDecided(tax)) {
    items.push({
      key: "BIR_WHT",
      label: "Withholding tax",
      text: tax.basisCents == null
        ? "Entered by HR after review."
        : `Taxable pay ${formatMoney(tax.basisCents)} — your pay after your SSS, PhilHealth and Pag-IBIG contributions.`,
      rule: tax.ruleTitle ? shortRuleName(tax.ruleTitle) : null,
      rounding: null,
    });
  }
  const sources = statutory?.lines?.[0]?.sources || [];
  return {
    items,
    monthLabel: statutory?.monthLabel || null,
    sources: sources.map((source) => `${source.periodName || "this payslip"} ${formatMoney(source.grossCents)}`),
  };
}

const days = (value) => {
  const number = Number(value || 0);
  return `${number} ${number === 1 ? "day" : "days"}`;
};

export function minutes(value) {
  const total = Number(value || 0);
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  return [hours ? `${hours} h` : null, rest || !hours ? `${rest} min` : null].filter(Boolean).join(" ");
}

/** The attendance payroll counted for this payslip. Only what was recorded; nothing when nothing was. */
export function attendanceFacts(payslip) {
  const facts = [
    ["Days worked", payslip?.worked_days, days],
    ["Days absent", payslip?.absent_days, days],
    ["Late", payslip?.late_minutes, minutes],
    ["Approved overtime", payslip?.overtime_minutes, minutes],
    ["Paid leave", payslip?.paid_leave_days, days],
    ["Unpaid leave", payslip?.unpaid_leave_days, days],
  ];
  return facts.filter(([, value]) => Number(value || 0) > 0).map(([label, value, format]) => ({ label, value: format(value) }));
}

/** The suspension recorded on a payslip ("Suspended from Oct 9 — 3 working days unpaid"), or null. */
export function suspensionNote(payslip) {
  const note = String(payslip?.notes || "").trim();
  return /^Suspended /.test(note) ? note.replace(/\.$/, "") : null;
}

export function employeeName(user) {
  return [user?.firstName, user?.lastName].map((part) => String(part || "").trim()).filter(Boolean).join(" ");
}

// APP-HR-103: who and what a payslip named when it was released. The server stores it once, at release; a payslip
// released before that was kept has none, and then today's names are shown and said to be today's.
export const NOT_STORED_AT_ISSUE = "as currently recorded — not stored when this payslip was issued";

const isIssued = (payslip) => ["released", "settled"].includes(String(payslip?.payslip_storage_status || payslip?.status || "").toLowerCase());

/**
 * `current`: today's employer and employee names, for a payslip that carries no issued details.
 * Returns the facts to print and whether they are the stored ones.
 */
export function issuedIdentity(payslip, current = {}) {
  const issuance = payslip?.issuance || null;
  if (issuance) {
    return {
      stored: true,
      legacy: false,
      employer: issuance.business_name || null,
      employee: issuance.employee_name || null,
      employeeCode: issuance.employee_code || null,
      position: issuance.position || null,
      department: issuance.department || null,
      issuedAt: issuance.issued_at || null,
    };
  }
  return {
    stored: false,
    // Released without stored details: an older payslip. Before release nothing has been issued yet.
    legacy: isIssued(payslip),
    employer: current.businessName || null,
    employee: current.employee || null,
    employeeCode: null,
    position: null,
    department: null,
    issuedAt: null,
  };
}
